import React, { Fragment, useState } from "react";
import styled from "@emotion/styled";
import {
  Badge,
  climatePositiveBadges,
  treeBadges,
  carbonReductionBadges,
  leaderboardBadges,
  sparklyBadges,
  User,
  LeaderboardStats,
} from "@ecologi/shared";
import { AchievementPopup } from "./AchievementPopup";
import { AchievementBadge } from "./AchievementBadge";
import { Button } from "@ecologi/react-components/src/Button";
import { Typography } from "../../Typography";
import { PageLoadingIndicator } from "@ecologi/react-components/src/PageLoadingIndicator";
import { BadgesPageHeader } from "@ecologi/react-components/src/PageHeaders";
import {
  BackgroundVariant,
  Section,
} from "@ecologi/react-components/src/Section";

const BadgesWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 auto;
  justify-content: center;
  align-content: center;
  width: 100%;
  gap: 24px;

  > div {
    width: 184px;
  }

  @media ${(props) => props.theme.bp.tabletUp} {
    max-width: 732px;
  }

  @media ${(props) => props.theme.bp.dashboardPageDesktop} {
    max-width: 1118px;
  }
`;

const TopBadgesWrapper = styled(BadgesWrapper)`
  gap: 24px;

  > div {
    width: 224px;
  }

  @media ${(props) => props.theme.bp.tabletUp} {
    gap: 48px;
  }

  @media ${(props) => props.theme.bp.desktopUp} {
    gap: 64px;
  }
`;

const LeaderboardLink = styled(Typography)`
  display: block;
  width: fit-content;
  margin: -24px auto 48px;
`;

interface AchievementsProps {
  user: User;
  isOwnProfile: boolean;
  leaderboardStats: LeaderboardStats | null;
}

export const Achievements = ({
  user,
  isOwnProfile,
  leaderboardStats,
}: AchievementsProps) => {
  const [achievementPopupBadge, setAchievementPopupBadge] =
    useState<Badge | null>(null);

  // Filter used to make sure we only show the right leaderboard
  // achievements for business and normal users
  const leaderboardBadgeFilter = (b: Badge) => {
    if (b.visibility === "all") {
      return true;
    }
    if (user && (user.isBusinessPlan || user.isSubscriptionless)) {
      return b.visibility === "business-only";
    } else {
      return b.visibility === "member-only";
    }
  };

  const hasBadges = user.achievements.length > 0;
  const badgesTitle = `${isOwnProfile ? "Your" : "Their"} ${
    hasBadges ? "top" : "next"
  } badges!`;
  const hasFourOrMoreBadges = user.achievements.length >= 4;
  const [topBadgeSliceStart, topBadgeSliceEnd] = hasFourOrMoreBadges
    ? [1, 4]
    : [0, 3];

  return (
    <Fragment>
      <BadgesPageHeader
        fullNamePlural={user.fullNamePlural}
        isOwnProfile={isOwnProfile}
      />
      {user && !user.isSubscriptionless && (
        <Fragment>
          <Section.Outer paddingTopOverride="0">
            <Section.Heading title={badgesTitle} marginBottomOverride="50px" />
            <TopBadgesWrapper>
              {user
                .getTop4Badges()
                .slice(topBadgeSliceStart, topBadgeSliceEnd)
                .map((badge, index) => {
                  return (
                    <AchievementBadge
                      badge={badge}
                      isStatic={!isOwnProfile}
                      key={badge.id + index}
                      setAchievementPopupBadge={setAchievementPopupBadge}
                      user={user}
                      size={224}
                    />
                  );
                })}
            </TopBadgesWrapper>
          </Section.Outer>
          <Section.Outer backgroundVariant={BackgroundVariant.GREY}>
            <Section.Heading title="Climate action" />
            <BadgesWrapper>
              {climatePositiveBadges.map((badge, index) => (
                <AchievementBadge
                  badge={badge}
                  isStatic={!isOwnProfile}
                  key={badge.id + index}
                  setAchievementPopupBadge={setAchievementPopupBadge}
                  user={user}
                  size={112}
                />
              ))}
            </BadgesWrapper>
          </Section.Outer>
        </Fragment>
      )}
      <Section.Outer>
        <Section.Heading title="Trees" />
        <BadgesWrapper>
          {[...treeBadges, ...sparklyBadges].map((badge, index) => (
            <AchievementBadge
              badge={badge}
              isStatic={!isOwnProfile}
              key={badge.id + index}
              setAchievementPopupBadge={setAchievementPopupBadge}
              user={user}
              size={112}
            />
          ))}
        </BadgesWrapper>
      </Section.Outer>
      <Section.Outer backgroundVariant={BackgroundVariant.GREY}>
        <Section.Heading title="Carbon avoidance" />
        <BadgesWrapper>
          {carbonReductionBadges.map((badge, index) => (
            <AchievementBadge
              badge={badge}
              isStatic={!isOwnProfile}
              key={badge.id + index}
              setAchievementPopupBadge={setAchievementPopupBadge}
              user={user}
              size={112}
            />
          ))}
        </BadgesWrapper>
      </Section.Outer>
      <Section.Outer>
        <Section.Heading title="Leaderboards" />
        {user && (
          <LeaderboardLink use="standaloneLink" as="a" href="/impact-shop">
            Buy more impact!
          </LeaderboardLink>
        )}
        <BadgesWrapper>
          {leaderboardBadges
            .filter(leaderboardBadgeFilter)
            .map((badge, index) => (
              <AchievementBadge
                badge={badge}
                isStatic={!isOwnProfile}
                key={badge.id + index}
                setAchievementPopupBadge={setAchievementPopupBadge}
                user={user}
                size={112}
              />
            ))}
        </BadgesWrapper>
      </Section.Outer>
      <Section.Outer paddingTopOverride="0px">
        <BadgesWrapper>
          <Button href="/leaderboards" text="See the leaderboards" primary />
        </BadgesWrapper>
      </Section.Outer>
      {achievementPopupBadge && (
        <AchievementPopup
          leaderboardStats={leaderboardStats}
          badge={achievementPopupBadge}
          user={user}
          onClose={() => {
            setAchievementPopupBadge(null);
          }}
        />
      )}
    </Fragment>
  );
};

const Loading = () => {
  return <PageLoadingIndicator text="Loading your badges..." />;
};

Achievements.Loading = Loading;
